import React, { useState, useEffect, useCallback } from 'react';
import { Play, ExternalLink } from 'lucide-react';

const FollowUpQuestions = ({ 
    selectedDocument, 
    user, 
    showAfterDelay = 500,
    className = "follow-up-questions-in-chat",
    headerClassName = "follow-up-header",
    questionsClassName = "questions-list-inline",
    triggerShow = false
}) => {
    const [followUpQuestions, setFollowUpQuestions] = useState([]);
    const [expandedQuestions, setExpandedQuestions] = useState({});
    const [questionAnswers, setQuestionAnswers] = useState({});
    const [questionLoading, setQuestionLoading] = useState({});
    const [showFollowUpQuestions, setShowFollowUpQuestions] = useState(false);

    // Generate follow-up questions from summary
    const generateFollowUpQuestions = (summary) => {
        if (!summary || !summary.next_steps?.questions) return [];
        
        return summary.next_steps.questions.map((question, index) => ({
            question: question,
            id: `follow_up_${Date.now()}_${index}`,
            type: 'follow_up'
        }));
    };

    // Handle question click
    const handleQuestionClick = async (question) => {
        if (questionLoading[question.id]) return;

        // Toggle expansion
        setExpandedQuestions(prev => ({
            ...prev,
            [question.id]: !prev[question.id]
        }));

        // If already answered, don't fetch again
        if (questionAnswers[question.id]) return;

        // Set loading state
        setQuestionLoading(prev => ({
            ...prev,
            [question.id]: true
        }));

        try {
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/answer_question', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    question: question.question,
                    user_id: user?.uid,
                    document_id: selectedDocument?.id || selectedDocument?.document_id || selectedDocument?.uuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get answer');
            }

            const data = await response.json();
            
            // Store the answer
            setQuestionAnswers(prev => ({
                ...prev,
                [question.id]: data
            }));

        } catch (error) {
            console.error('Question answer error:', error);
            // Store error state
            setQuestionAnswers(prev => ({
                ...prev,
                [question.id]: {
                    answer: 'Sorry, I encountered an error while answering this question. Please try again.',
                    sources: { documents: [], web: [] },
                    confidence: 'low'
                }
            }));
        } finally {
            setQuestionLoading(prev => ({
                ...prev,
                [question.id]: false
            }));
        }
    };

    // Load follow-up questions when document changes
    useEffect(() => {
        if (selectedDocument?.summary) {
            const questions = generateFollowUpQuestions(selectedDocument.summary);
            setFollowUpQuestions(questions);
            // Reset states when document changes
            setExpandedQuestions({});
            setQuestionAnswers({});
            setQuestionLoading({});
            setShowFollowUpQuestions(false);
        } else {
            // TEMPORARY: Show sample questions for testing
            const sampleQuestions = [
                {
                    question: "What are the key benefits of effective file naming strategies?",
                    id: 'sample_1',
                    type: 'follow_up'
                },
                {
                    question: "How can I improve my file organization system?",
                    id: 'sample_2',
                    type: 'follow_up'
                },
                {
                    question: "What are the best practices for managing digital files?",
                    id: 'sample_3',
                    type: 'follow_up'
                },
                {
                    question: "How does file naming impact productivity and efficiency?",
                    id: 'sample_4',
                    type: 'follow_up'
                }
            ];
            setFollowUpQuestions(sampleQuestions);
        }
    }, [selectedDocument]);

    // Show questions after delay (for chat integration)
    const showQuestions = useCallback(() => {
        console.log('FollowUpQuestions - showQuestions called');
        console.log('FollowUpQuestions - followUpQuestions.length:', followUpQuestions.length);
        if (followUpQuestions.length > 0) {
            console.log('FollowUpQuestions - Setting showFollowUpQuestions to true after delay');
            setTimeout(() => {
                setShowFollowUpQuestions(true);
            }, showAfterDelay);
        } else {
            console.log('FollowUpQuestions - No questions to show');
        }
    }, [followUpQuestions, showAfterDelay]);

    // Watch for triggerShow prop
    useEffect(() => {
        if (triggerShow && followUpQuestions.length > 0) {
            console.log('FollowUpQuestions - triggerShow is true, showing questions');
            setTimeout(() => {
                setShowFollowUpQuestions(true);
            }, showAfterDelay);
        }
    }, [triggerShow, followUpQuestions.length, showAfterDelay]);

    // Expose showQuestions function to parent components via global function
    useEffect(() => {
        console.log('FollowUpQuestions - Setting global function');
        window.showFollowUpQuestions = showQuestions;
        return () => {
            console.log('FollowUpQuestions - Cleaning up global function');
            delete window.showFollowUpQuestions;
        };
    }, [showQuestions]);

    console.log('FollowUpQuestions - Render check:', {
        showFollowUpQuestions,
        followUpQuestionsLength: followUpQuestions.length,
        selectedDocument: selectedDocument?.title || selectedDocument?.name || 'No title'
    });

    if (!showFollowUpQuestions || followUpQuestions.length === 0) {
        console.log('FollowUpQuestions - Not rendering:', { showFollowUpQuestions, followUpQuestionsLength: followUpQuestions.length });
        return null;
    }

    return (
        <div className={`${className} fade-in`}>
            <div className={headerClassName}>
                <span className="follow-up-icon">💡</span>
                <span className="follow-up-text">Follow-up questions</span>
            </div>
            <div className={questionsClassName}>
                {followUpQuestions.map((question) => (
                    <div key={question.id} className="question-card-inline">
                        <div 
                            className="question-header-inline"
                            onClick={() => handleQuestionClick(question)}
                        >
                            <div className="question-text-inline">
                                {question.question}
                            </div>
                            <div className="question-play-icon-inline">
                                <Play size={14} />
                            </div>
                        </div>
                        
                        {expandedQuestions[question.id] && (
                            <div className="question-answer-inline">
                                {questionLoading[question.id] ? (
                                    <div className="answer-loading-inline">
                                        <div className="loading-skeleton-inline">
                                            <div className="skeleton-line-inline skeleton-title-inline"></div>
                                            <div className="skeleton-line-inline skeleton-text-inline"></div>
                                            <div className="skeleton-line-inline skeleton-text-inline"></div>
                                            <div className="skeleton-line-inline skeleton-text-short-inline"></div>
                                        </div>
                                    </div>
                                ) : questionAnswers[question.id] ? (
                                    <div className="answer-content-inline">
                                        <div className="answer-text-inline">
                                            {questionAnswers[question.id].answer}
                                        </div>
                                        
                                        {/* Sources */}
                                        {(questionAnswers[question.id].sources?.documents?.length > 0 || 
                                          questionAnswers[question.id].sources?.web?.length > 0) && (
                                            <div className="answer-sources-inline">
                                                <h4>Sources:</h4>
                                                
                                                {/* Document Sources */}
                                                {questionAnswers[question.id].sources?.documents?.length > 0 && (
                                                    <div className="source-group-inline">
                                                        <h5>📄 Your Documents</h5>
                                                        {questionAnswers[question.id].sources.documents.map((doc, docIndex) => (
                                                            <div key={docIndex} className="source-item-inline document-source-inline">
                                                                <span className="source-title-inline">{doc.title}</span>
                                                                <span className="source-relevance-inline">{doc.relevance}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Web Sources */}
                                                {questionAnswers[question.id].sources?.web?.length > 0 && (
                                                    <div className="source-group-inline">
                                                        <h5>🌐 Web Sources</h5>
                                                        {questionAnswers[question.id].sources.web.map((source, sourceIndex) => (
                                                            <div key={sourceIndex} className="source-item-inline web-source-inline">
                                                                <a 
                                                                    href={source.url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="source-link-inline"
                                                                >
                                                                    <span className="source-title-inline">{source.title}</span>
                                                                    <ExternalLink size={12} />
                                                                </a>
                                                                <div className="source-snippet-inline">{source.snippet}</div>
                                                                <span className="source-relevance-inline">{source.relevance}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Confidence */}
                                        {questionAnswers[question.id].confidence && (
                                            <div className="answer-confidence-inline">
                                                <span className={`confidence-badge-inline ${questionAnswers[question.id].confidence}`}>
                                                    Confidence: {questionAnswers[question.id].confidence}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FollowUpQuestions;
